import { keepPreviousData } from "@tanstack/react-query";
import camelCase from "lodash/camelCase";
import flatten from "lodash/flatten";
import map from "lodash/map";
import { CHAT_NOTIFICATION_CATEGORIES } from "PFApp/constants/notifications";
import { Accordion } from "PFComponents/accordion";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { CountTag } from "PFComponents/count_tag";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { useNotificationsGroupsInfinite } from "PFCore/hooks/queries/notifications/use_notifications_groups";
import { NotificationGroupType } from "PFCore/types";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { humanize } from "underscore.string";

import { useNotificationsPanelContext } from "../contexts/notifications_panel_context";
import { NotificationsPanelList } from "../notifications_panel_list";
import { NotificationsEmptyState } from "../parts/notifications_empty_state";
import css from "./notifications_panel_groups.module.scss";

export const NotificationsPanelGroups = () => {
  const { t } = useTranslation("translation");
  const { t: tNotifications } = useTranslation("notificationsSettings");
  const { groupBy, role, engagement, categories, showUnreadOnly, showImportantOnly } =
    useNotificationsPanelContext();

  const {
    data: groupPages,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage
  } = useNotificationsGroupsInfinite(
    {
      perPage: 25,
      groupType: groupBy,
      roleId: role?.id,
      engagementId: engagement?.id,
      categories: categories.length ? categories : undefined,
      read: showUnreadOnly ? false : undefined,
      important: showImportantOnly ? true : undefined,
      excludedCategories:
        groupBy === NotificationGroupType.Category ? CHAT_NOTIFICATION_CATEGORIES : undefined
    },
    { placeholderData: keepPreviousData }
  );
  const groups = flatten(map(groupPages?.pages, "entries"));

  if (groups.length === 0 && !isLoading) {
    return <NotificationsEmptyState />;
  }

  return (
    <Stack gap="spacingLg" className={css.groupsList} center>
      {isLoading && <LoadingDots circlesEnabled circleSize="sm" />}
      {groups.map((group, index) => {
        const groupName =
          groupBy === NotificationGroupType.Category
            ? tNotifications(`category.${camelCase(group.name)}` as unknown as TemplateStringsArray, {
                defaultValue: humanize(group.name)
              })
            : group.name;

        return (
          <Fragment key={group.identifier}>
            <Accordion
              header={
                <Inline alignItems="center" className={css.header}>
                  <div className={css.groupName} title={groupName}>
                    {groupName}
                  </div>
                  <CountTag count={group.notificationsCount} highlight />
                </Inline>
              }
              headerVariant="bodyBold"
              iconSet="chevron"
              defaultState="closed"
              headerFullWidth
            >
              <NotificationsPanelList
                groupFilters={{
                  ...(groupBy === NotificationGroupType.EngagementId
                    ? { engagementId: group.identifier }
                    : {}),
                  ...(groupBy === NotificationGroupType.RoleId ? { roleId: group.identifier } : {}),
                  ...(groupBy === NotificationGroupType.Category
                    ? { categories: [group.identifier as string] }
                    : {})
                }}
              />
            </Accordion>
            {index < groups.length - 1 && <Divider />}
          </Fragment>
        );
      })}
      {hasNextPage && !isFetchingNextPage && (
        <Button kind="text" onClick={() => fetchNextPage()}>
          {t("notifications.showMoreGroups")}
        </Button>
      )}
      {isFetchingNextPage && <LoadingDots circlesEnabled circleSize="sm" />}
    </Stack>
  );
};
